import React, { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Loader2,
  Plus,
  QrCode,
  Users,
  Package,
  AlertCircle,
  CreditCard,
} from "lucide-react";
import { StudentSelector } from "@/components/business/family/StudentSelector";
import { ProductSelector } from "@/components/business/products/ProductSelector";
import { PurchaseListCard } from "./PurchaseListCard";
// import { AssignmentHistory } from "./AssignmentHistory";
import { purchaseListService, userService, productService } from "@/services";
import {
  PurchaseListResponse,
  AssignmentResponse,
} from "@/types/purchaseList.types";
interface StudentSearchResult {
  id: string;
  name: string;
  nombre: string;
  apellido: string;
  email: string;
  rut: string;
  activo: boolean;
}
import { IProduct } from "@/services/mocks/productData";

interface PurchaseListManagerProps {
  className?: string;
}

const PurchaseListManager: React.FC<PurchaseListManagerProps> = ({
  className,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();

  // Estados
  const [purchaseLists, setPurchaseLists] = useState<PurchaseListResponse[]>(
    []
  );
  const [_assignments, _setAssignments] = useState<AssignmentResponse[]>([]);
  const [, setProducts] = useState<IProduct[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [, setIsLoadingProducts] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estados del formulario
  const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
  const [selectedStudent, setSelectedStudent] =
    useState<StudentSearchResult | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Cargar datos iniciales
  useEffect(() => {
    if (user?.id) {
      loadPurchaseLists();
      loadAssignments();
      loadProducts();
    }
  }, [user?.id]);

  // Cargar productos disponibles
  const loadProducts = async () => {
    setIsLoadingProducts(true);
    try {
      const response = await productService.getAllProducts();
      // Filtrar solo productos que pueden ser canjeables (tickets/credits)
      const canjeableProducts = response.products.filter(
        (product) =>
          (product.disponible &&
            product.nombre.toLowerCase().includes("ticket")) ||
          product.nombre.toLowerCase().includes("almuerzo") ||
          product.nombre.toLowerCase().includes("credito")
      );
      setProducts(canjeableProducts);
    } catch (error: any) {
      console.error("Error cargando productos:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los productos",
        variant: "destructive",
      });
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Cargar listas de compra del apoderado
  const loadPurchaseLists = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const lists = await purchaseListService.getPurchaseListsByApoderado(
        // user.id
      );
      setPurchaseLists(lists);
    } catch (error: any) {
      setError("Error al cargar las listas de compra");
      toast({
        title: "Error",
        description: "No se pudieron cargar las listas de compra",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar asignaciones
  const loadAssignments = async () => {
    if (!user?.id) return;

    try {
      // Obtener hijos del apoderado
      const children = await userService.getApoderadoChildren();

      // Cargar asignaciones para cada hijo
      const allAssignments: AssignmentResponse[] = [];
      for (const child of children.children) {
        try {
          const childAssignments =
            await purchaseListService.getStudentAssignments(
              child.estudiante_id
            );
          allAssignments.push(...childAssignments);
        } catch (error) {
          console.error(
            `Error cargando asignaciones para ${child.estudiante_id}:`,
            error
          );
        }
      }

      _setAssignments(allAssignments);
    } catch (error: any) {
      console.error("Error cargando asignaciones:", error);
      setError(error.message);
    }
  };

  // Crear nueva lista de compra
  const handleCreatePurchaseList = async () => {
    if (!user?.id || !selectedProduct || !selectedStudent) {
      toast({
        title: "Error",
        description: "Por favor completa todos los campos",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      // Crear lista de compra
      const purchaseList = await purchaseListService.createPurchaseList({
        apoderado_id: user.id,
        producto_id_canjeable: selectedProduct.id,
        pedido_id: `order-${Date.now()}`, // Temporal, debería venir del carrito
        saldo_total_comprado: quantity,
      });

      // Asignar productos al estudiante
      await purchaseListService.assignProductsToStudent(purchaseList.id, {
        estudiante_id: selectedStudent.id,
        cantidad_asignada: quantity,
      });

      // Generar QR
      await purchaseListService.generateQR(purchaseList.id, {});

      toast({
        title: "Éxito",
        description: "Lista de compra creada y asignada correctamente",
      });

      // Recargar datos
      await loadPurchaseLists();
      await loadAssignments();

      // Limpiar formulario
      setSelectedProduct(null);
      setSelectedStudent(null);
      setQuantity(1);
      setIsDialogOpen(false);
    } catch (error: any) {
      setError("Error al crear la lista de compra");
      toast({
        title: "Error",
        description: "No se pudo crear la lista de compra",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Generar QR para una lista de compra
  const handleGenerateQR = async (purchaseListId: string) => {
    try {
      await purchaseListService.generateQR(purchaseListId, {});
      toast({
        title: "Éxito",
        description: "Código QR generado correctamente",
      });
      await loadPurchaseLists();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudo generar el código QR",
        variant: "destructive",
      });
    }
  };

  // Calcular precio total
  const getTotalPrice = () => {
    return selectedProduct ? selectedProduct.precio * quantity : 0;
  };

  return (
    <div className={className}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Gestión de Listas de Compra
            </CardTitle>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Nueva Lista
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Crear Nueva Lista de Compra</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Seleccionar Producto */}
                  <div className="space-y-2">
                    <Label>Producto</Label>
                    <ProductSelector
                      onProductSelect={setSelectedProduct}
                      selectedProduct={selectedProduct}
                      disabled={isCreating}
                    />
                  </div>

                  {/* Seleccionar Estudiante */}
                  <div className="space-y-2">
                    <Label>Estudiante</Label>
                    <StudentSelector
                      onStudentSelect={(student) => setSelectedStudent({
                        id: student.id,
                        name: student.nombre,
                        nombre: student.nombre,
                        apellido: student.apellido,
                        email: student.email || '',
                        rut: student.rut || '',
                        activo: student.activo
                      })}
                      selectedStudent={selectedStudent}
                    />
                  </div>

                  {/* Cantidad */}
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Cantidad</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) =>
                        setQuantity(parseInt(e.target.value) || 1)
                      }
                    />
                  </div>

                  {/* Resumen del pedido */}
                  {selectedProduct && selectedStudent && quantity > 0 && (
                    <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                      <h4 className="font-medium text-primary mb-2">
                        Resumen del Pedido
                      </h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>Producto:</span>
                          <span>{selectedProduct.nombre}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Estudiante:</span>
                          <span>
                            {selectedStudent.nombre} {selectedStudent.apellido}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Cantidad:</span>
                          <span>{quantity}</span>
                        </div>
                        <div className="flex justify-between font-medium text-primary">
                          <span>Total:</span>
                          <span>
                            ${getTotalPrice().toLocaleString("es-CL")}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Botones */}
                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                      disabled={isCreating}
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleCreatePurchaseList}
                      disabled={
                        isCreating ||
                        !selectedProduct ||
                        !selectedStudent ||
                        quantity < 1
                      }
                    >
                      {isCreating && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Crear Lista
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="lists" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="lists" className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Listas
              </TabsTrigger>
              <TabsTrigger
                value="assignments"
                className="flex items-center gap-2"
              >
                <Users className="h-4 w-4" />
                Asignaciones
              </TabsTrigger>
              <TabsTrigger value="qr-codes" className="flex items-center gap-2">
                <QrCode className="h-4 w-4" />
                QR Codes
              </TabsTrigger>
            </TabsList>

            <TabsContent value="lists" className="space-y-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : error ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : purchaseLists.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No hay listas de compra creadas</p>
                  <p className="text-sm">
                    Crea tu primera lista de compra para comenzar
                  </p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {purchaseLists.map((purchaseList) => (
                    <PurchaseListCard
                      key={purchaseList.id}
                      purchaseList={purchaseList}
                      onGenerateQR={() => handleGenerateQR(purchaseList.id)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="assignments" className="space-y-4">
              <div className="p-4 text-center text-muted-foreground">
                Componente AssignmentHistory no implementado
              </div>
            </TabsContent>

            <TabsContent value="qr-codes" className="space-y-4">
              <div className="grid gap-4">
                {purchaseLists
                  .filter((list) => list.qr_code)
                  .map((purchaseList) => (
                    <Card key={purchaseList.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">
                              {purchaseList.producto?.nombre || "Producto"}
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              QR: {purchaseList.qr_code}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Saldo: {purchaseList.saldo_restante} /{" "}
                              {purchaseList.saldo_total_comprado}
                            </p>
                          </div>
                          <Badge
                            variant={
                              purchaseList.is_active ? "default" : "secondary"
                            }
                          >
                            {purchaseList.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default PurchaseListManager;
